import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react'
import { api } from '../api/client.js'
import { useLocale } from '../locale/LocaleContext.js'

type ImageState =
  | { key: string | null; status: 'idle' | 'loading'; objectUrl: null }
  | { key: string; status: 'ready'; objectUrl: string }
  | { key: string; status: 'error'; objectUrl: null }

export interface AuthorizedMediaImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src'
> {
  source: string
  token?: string | null
}

export function AuthorizedMediaImage({
  source,
  token,
  loading,
  className,
  alt = '',
  ...imageProps
}: AuthorizedMediaImageProps) {
  const { t } = useLocale()
  const hostRef = useRef<HTMLSpanElement | null>(null)
  const requestKey = `${source}\u0000${token ?? ''}`
  const [visibleKey, setVisibleKey] = useState<string | null>(
    loading === 'lazy' ? null : requestKey,
  )
  const [state, setState] = useState<ImageState>({
    key: null,
    status: 'idle',
    objectUrl: null,
  })
  const visible = loading !== 'lazy' || visibleKey === requestKey
  const currentState =
    state.key === requestKey ? state : ({ key: null, status: 'idle', objectUrl: null } as const)

  useEffect(() => {
    if (loading !== 'lazy') {
      setVisibleKey(requestKey)
      return
    }
    const host = hostRef.current
    if (!host || typeof IntersectionObserver === 'undefined') {
      setVisibleKey(requestKey)
      return
    }
    setVisibleKey(null)
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleKey(requestKey)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(host)
    return () => observer.disconnect()
  }, [loading, requestKey])

  useEffect(() => {
    if (!visible) return
    const controller = new AbortController()
    let objectUrl: string | null = null
    setState({ key: requestKey, status: 'loading', objectUrl: null })
    void api
      .getMediaImage(source, token, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return
        objectUrl = URL.createObjectURL(blob)
        setState({ key: requestKey, status: 'ready', objectUrl })
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          (error instanceof DOMException && error.name === 'AbortError')
        ) {
          return
        }
        setState({ key: requestKey, status: 'error', objectUrl: null })
      })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [requestKey, source, token, visible])

  return (
    <span ref={hostRef} className={`relative block ${className ?? ''}`}>
      {currentState.status === 'ready' ? (
        <img
          {...imageProps}
          src={currentState.objectUrl}
          alt={alt}
          className="h-full w-full"
          style={{ ...imageProps.style, objectFit: 'inherit' }}
        />
      ) : currentState.status === 'error' ? (
        <span
          role="alert"
          className="flex h-full w-full items-center justify-center bg-[var(--xr-bg-surface-elev)] px-2 text-center text-[10px] text-[var(--xr-text-muted)]"
          data-testid="authorized-media-error"
        >
          {t('mediaLoadError')}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="block h-full w-full animate-pulse bg-[var(--xr-bg-surface-elev)]"
        />
      )}
    </span>
  )
}
